import { Close } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Questionnaire } from 'fhir/r4b';
import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSelect, FormTextField, LabeledField, Option } from 'src/components/form';
import { BasicDatePicker } from 'src/components/form/DatePicker';
import { RoundedButton } from 'src/components/RoundedButton';
import { FormFields, RELATIONSHIP_TO_INSURED_OPTIONS, SEX_OPTIONS, STATE_OPTIONS } from 'src/constants';
import { dataTestIds } from 'src/constants/data-test-ids';
import { structureQuestionnaireResponse } from 'src/helpers/qr-structure';
import { useUpdatePatientAccount } from 'src/hooks/useGetPatient';
import { usePatientStore } from 'src/state/patient.store';
import {
  InsurancePlanDTO,
  InsurancePlanType,
  InsurancePlanTypes,
  isPostalCodeValid,
  REQUIRED_FIELD_ERROR_MESSAGE,
} from 'utils';

interface AddInsuranceModalProps {
  open: boolean;
  patientId: string;
  questionnaire: Questionnaire;
  priorityOptions: { value: string; label: string }[];
  onClose: () => void;
}

const insurance = FormFields.insurance[0];

export const AddInsuranceModal: React.FC<AddInsuranceModalProps> = ({
  open,
  patientId,
  questionnaire,
  priorityOptions,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      [insurance.insurancePriority.key]: priorityOptions[0]?.value || 'Primary',
      [insurance.insuranceCarrier.key]: null,
      [insurance.insurancePlanType.key]: null,
      [insurance.memberId.key]: '',
      [insurance.firstName.key]: '',
      [insurance.middleName.key]: '',
      [insurance.lastName.key]: '',
      [insurance.birthDate.key]: '',
      [insurance.birthSex.key]: '',
      [insurance.relationship.key]: '',
      [insurance.streetAddress.key]: '',
      [insurance.addressLine2.key]: '',
      [insurance.city.key]: '',
      [insurance.state.key]: null,
      [insurance.zip.key]: '',
      [insurance.additionalInformation.key]: '',
    } as any,
  });

  const { control, formState, handleSubmit, setValue } = methods;
  const { errors, defaultValues } = formState;

  const { insurancePlans } = usePatientStore();
  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isLoading,
    isIdle,
    reset,
    isSuccess,
  } = useUpdatePatientAccount(() => {
    void queryClient.invalidateQueries({ queryKey: ['patient-coverages'] });
  }, t('patientProfile.insurance.success'));

  const onSubmit = (data: any): void => {
    // send the data to a zambda

    const questionnaireResponse = structureQuestionnaireResponse(questionnaire, data, patientId);
    mutate(questionnaireResponse);
  };

  useEffect(() => {
    if (!open) {
      methods.reset();
    }
  }, [open, methods]);

  useEffect(() => {
    if (priorityOptions.length === 1 && priorityOptions[0]?.value) {
      methods.setValue(insurance.insurancePriority.key, priorityOptions[0].value);
    }
  }, [priorityOptions, methods]);

  useEffect(() => {
    if (!open && !isIdle) {
      reset();
    } else if (open && isSuccess) {
      onClose();
    }
  }, [open, onClose, isIdle, isSuccess, reset]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { p: 2, maxWidth: 'none' } }}
      data-testid={dataTestIds.addInsuranceDialog.id}
    >
      <DialogTitle
        variant="h5"
        color="secondary.main"
        sx={{
          width: '100%',
          fontSize: '24px',
          color: theme.palette.primary.dark,
          fontWeight: '600 !important',
        }}
      >
        {t('patientProfile.buttons.addInsurance')}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <FormProvider {...methods}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledField
                label={t('patientProfile.fields.insuranceType')}
                required
                error={!!errors[insurance.insurancePriority.key]}
              >
                <FormSelect
                  data-testid={dataTestIds.addInsuranceDialog.type}
                  name={insurance.insurancePriority.key}
                  variant="outlined"
                  control={control}
                  options={priorityOptions}
                  defaultValue={defaultValues?.insurancePriority || 'Primary'}
                  disabled={priorityOptions.length === 1}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledField
                label={t('patientProfile.fields.insuranceCarrier')}
                required
                error={!!errors[insurance.insuranceCarrier.key]}
              >
                <Controller
                  name={insurance.insuranceCarrier.key}
                  control={control}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                    validate: (value) =>
                      insurancePlans.some((option) => `Organization/${option.id}` === value?.reference),
                  }}
                  render={({ field: { value }, fieldState: { error } }) => {
                    const isLoading = insurancePlans.length === 0;

                    const selectedOption = insurancePlans.find(
                      (option) => `Organization/${option.id}` === value?.reference
                    );
                    return (
                      <Autocomplete
                        options={insurancePlans}
                        loading={isLoading}
                        loadingText={t('patientProfile.text.loading')}
                        value={selectedOption ?? ({} as InsurancePlanDTO)}
                        isOptionEqualToValue={(option, value) => {
                          return option?.id === value?.id;
                        }}
                        getOptionLabel={(option) =>
                          option.payerId || option.name ? `${option?.payerId || 'N/A'} - ${option?.name || 'N/A'}` : ''
                        }
                        onChange={(_, newValue) => {
                          if (newValue) {
                            setValue(
                              insurance.insuranceCarrier.key,
                              { reference: `Organization/${newValue.id}`, display: newValue.name },
                              { shouldDirty: true }
                            );
                          } else {
                            setValue(insurance.insuranceCarrier.key, null);
                          }
                        }}
                        disableClearable
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!error}
                            required
                            helperText={error?.message}
                            data-testid={dataTestIds.addInsuranceDialog.insuranceCarrier}
                          />
                        )}
                      />
                    );
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledField
                label={t('patientProfile.fields.insurancePlanType')}
                error={!!errors[insurance.insurancePlanType.key]}
              >
                <Controller
                  name={insurance.insurancePlanType.key}
                  control={control}
                  rules={{
                    validate: (value) => !value || InsurancePlanTypes.some((option) => option.candidCode === value),
                  }}
                  render={({ field: { value }, fieldState: { error } }) => {
                    const selectedOption = InsurancePlanTypes.find((option) => option.candidCode === value);
                    return (
                      <Autocomplete
                        options={InsurancePlanTypes}
                        value={selectedOption ?? ({} as InsurancePlanType)}
                        isOptionEqualToValue={(option, value) => option?.candidCode === value?.candidCode}
                        getOptionLabel={(option) =>
                          option.candidCode || option.label ? `${option.candidCode} - ${option.label}` : ''
                        }
                        onChange={(_, newValue) => {
                          if (newValue) {
                            setValue(insurance.insurancePlanType.key, newValue.candidCode, { shouldDirty: true });
                          } else {
                            setValue(insurance.insurancePlanType.key, null);
                          }
                        }}
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!error}
                            helperText={error?.message}
                            data-testid={dataTestIds.addInsuranceDialog.planType}
                          />
                        )}
                      />
                    );
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.memberId}>
              <LabeledField
                label={t('patientProfile.fields.memberId')}
                required
                error={!!errors[insurance.memberId.key]}
              >
                <FormTextField
                  variant="outlined"
                  name={insurance.memberId.key}
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                  }}
                />
              </LabeledField>
            </Grid>
            {/* Empty grid item to maintain proper column alignment after adding the insurance type field */}
            <Grid item xs={false} md={8}></Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.policyHoldersFirstName}>
              <LabeledField
                label={t('patientProfile.fields.policyHoldersFirstName')}
                required
                error={!!errors[insurance.firstName.key]}
              >
                <FormTextField
                  variant="outlined"
                  name={insurance.firstName.key}
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.policyHoldersMiddleName}>
              <LabeledField label={t('patientProfile.fields.policyHoldersMiddleName')}>
                <FormTextField
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontWeight: 'bold',
                    },
                  }}
                  variant="outlined"
                  name={insurance.middleName.key}
                  control={control}
                  defaultValue={''}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.policyHoldersLastName}>
              <LabeledField
                label={t('patientProfile.fields.policyHoldersLastName')}
                required
                error={!!errors[insurance.lastName.key]}
              >
                <FormTextField
                  variant="outlined"
                  name={insurance.lastName.key}
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledField
                label={t('patientProfile.fields.policyHoldersDateOfBirth')}
                required
                error={!!errors[insurance.birthDate.key]}
              >
                <BasicDatePicker
                  name={insurance.birthDate.key}
                  variant="outlined"
                  control={control}
                  rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
                  defaultValue={''}
                  dataTestId={dataTestIds.addInsuranceDialog.policyHoldersDateOfBirth}
                  component="Field"
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.policyHoldersSex}>
              <LabeledField
                label={t('patientProfile.fields.policyHoldersSex')}
                required
                error={!!errors[insurance.birthSex.key]}
              >
                <FormSelect
                  variant="outlined"
                  name={insurance.birthSex.key}
                  control={control}
                  defaultValue={''}
                  options={SEX_OPTIONS}
                  rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.relationship}>
              <LabeledField
                label={t('patientProfile.fields.relationshipToInsured')}
                required
                error={!!errors[insurance.relationship.key]}
              >
                <FormSelect
                  variant="outlined"
                  name={insurance.relationship.key}
                  control={control}
                  defaultValue={''}
                  options={RELATIONSHIP_TO_INSURED_OPTIONS}
                  rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.streetAddress}>
              <LabeledField
                label={t('patientProfile.fields.streetAddress')}
                required
                error={!!errors[insurance.streetAddress.key]}
              >
                <FormTextField
                  placeholder={t('patientProfile.text.noNumberStreet')}
                  variant="outlined"
                  name={insurance.streetAddress.key}
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.addressLine2}>
              <LabeledField label={t('patientProfile.fields.addressLine2')}>
                <FormTextField
                  placeholder={t('patientProfile.text.noNumberStreet')}
                  variant="outlined"
                  name={insurance.addressLine2.key}
                  control={control}
                  defaultValue={''}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.city}>
              <LabeledField
                label={t('patientProfile.fields.cityStateZip')}
                required
                error={!!errors[insurance.city.key]}
              >
                <FormTextField
                  variant="outlined"
                  name={insurance.city.key}
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabeledField
                label={t('patientProfile.fields.cityStateZip')}
                required
                error={!!errors[insurance.state.key]}
              >
                <Controller
                  name={insurance.state.key}
                  control={control}
                  defaultValue={null}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                    validate: (value) => !value || STATE_OPTIONS.some((option) => option.value === value),
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Autocomplete
                      data-testid={dataTestIds.addInsuranceDialog.state}
                      options={STATE_OPTIONS}
                      value={(STATE_OPTIONS.find((option) => option.value === value) || null) as Option}
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, value) => {
                        return option?.value === value?.value || (!option && !value);
                      }}
                      onChange={(_, newValue) => {
                        onChange(newValue?.value || null);
                      }}
                      disableClearable
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          error={!!error}
                          placeholder={t('patientProfile.text.select')}
                          helperText={error?.message}
                        />
                      )}
                    />
                  )}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid={dataTestIds.addInsuranceDialog.zip}>
              <LabeledField
                label={t('patientProfile.fields.cityStateZip')}
                required
                error={!!errors[insurance.zip.key]}
              >
                <FormTextField
                  variant="outlined"
                  name={insurance.zip.key}
                  control={control}
                  defaultValue={''}
                  rules={{
                    required: REQUIRED_FIELD_ERROR_MESSAGE,
                    validate: (value: string) => isPostalCodeValid(value) || t('patientProfile.errors.zipDigits'),
                  }}
                />
              </LabeledField>
            </Grid>
            <Grid item xs={12} data-testid={dataTestIds.addInsuranceDialog.additionalInformation}>
              <LabeledField label={t('patientProfile.fields.additionalInformation')}>
                <FormTextField
                  variant="outlined"
                  name={insurance.additionalInformation.key}
                  control={control}
                  defaultValue={''}
                />
              </LabeledField>
            </Grid>
          </Grid>
        </DialogContent>
      </FormProvider>
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="primary"
          sx={{
            borderRadius: 25,
            textTransform: 'none',
            fontSize: 15,
            fontWeight: 'bold',
          }}
          onClick={onClose}
        >
          {t('patientProfile.text.cancel')}
        </Button>
        <RoundedButton
          data-testid={dataTestIds.addInsuranceDialog.addInsuranceButton}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: 25,
            textTransform: 'none',
            fontSize: 15,
            fontWeight: 'bold',
          }}
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
        >
          {t('patientProfile.buttons.addInsurance')}
        </RoundedButton>
      </DialogActions>
    </Dialog>
  );
};
